import {
    FavoriteRecipesActions,
    FavoriteRecipesActionTypes,
} from "../actions/favoriteRecipesActionCreators"

import { FavoriteRecipesData, FavoriteRecipesState } from "../../types"

const initialState: FavoriteRecipesState = {
    loading: false,
    data: {
        favoredRecipes: [],
        purchasedRecipes: [],
    },
    error: null,
    create: {
        loading: false,
        error: null,
    },
    remove: {
        loading: false,
        error: null,
    },
}

/**
 * Flags a recipe as removed from favorites or not, without actually removing it. This allows it to still be displayed
 * in the list of favorites so you can toggle it, and it will disappear from the list the next time favorite recipes
 * are loaded.
 * @param data Data object containing the favorite recipes
 * @param recipeId ID of the recipe to change
 * @param removed Removed status
 */
const setRemoved = (
    data: FavoriteRecipesData,
    recipeIds: string[],
    removed: boolean,
): FavoriteRecipesData => {
    const favoredRecipes = data.favoredRecipes.map(recipe => {
        if (recipeIds.includes(recipe.id)) {
            return {
                ...recipe,
                removed: removed,
            }
        }
        return recipe
    })

    return {
        ...data,
        favoredRecipes,
    }
}

const favoriteRecipesReducer = (
    state = initialState,
    action: FavoriteRecipesActions,
): FavoriteRecipesState => {
    switch (action.type) {
        // Get all
        case FavoriteRecipesActionTypes.GetAllFavoriteRecipesLoading:
            return { ...state, loading: true }
        case FavoriteRecipesActionTypes.GetAllFavoriteRecipesLoaded:
            return { ...state, loading: false }
        case FavoriteRecipesActionTypes.GetAllFavoriteRecipesSuccess:
            return { ...state, data: action.payload, error: null }
        case FavoriteRecipesActionTypes.GetAllFavoriteRecipesFromCache:
            return { ...state, data: action.payload, error: null }
        case FavoriteRecipesActionTypes.GetAllFavoriteRecipesError:
            return { ...state, error: action.payload }

        // Create
        case FavoriteRecipesActionTypes.CreateFavoriteRecipeLoading:
            return { ...state, create: { ...state.create, loading: action.payload } }
        case FavoriteRecipesActionTypes.CreateFavoriteRecipeLoaded:
            return { ...state, create: { ...state.create, loading: false } }
        case FavoriteRecipesActionTypes.CreateFavoriteRecipeSuccess:
            return {
                ...state,
                data: setRemoved(state.data, action.payload, false),
                create: { ...state.create, error: null },
            }
        case FavoriteRecipesActionTypes.CreateFavoriteRecipeError:
            return { ...state, create: { ...state.create, error: action.payload } }

        // Create recipes
        case FavoriteRecipesActionTypes.CreateFavoriteRecipesLoading:
            return { ...state, create: { ...state.create, loading: action.payload } }
        case FavoriteRecipesActionTypes.CreateFavoriteRecipesLoaded:
            return { ...state, create: { ...state.create, loading: false } }
        case FavoriteRecipesActionTypes.CreateFavoriteRecipesSuccess:
            return {
                ...state,
                data: setRemoved(state.data, action.payload, false),
                create: { ...state.create, error: null },
            }
        case FavoriteRecipesActionTypes.CreateFavoriteRecipesError:
            return { ...state, create: { ...state.create, error: action.payload } }

        // Remove
        case FavoriteRecipesActionTypes.RemoveFavoriteRecipeLoading:
            return { ...state, remove: { ...state.remove, loading: action.payload } }
        case FavoriteRecipesActionTypes.RemoveFavoriteRecipeLoaded:
            return { ...state, remove: { ...state.remove, loading: false } }
        case FavoriteRecipesActionTypes.RemoveFavoriteRecipeSuccess:
            return {
                ...state,
                data: setRemoved(state.data, [action.payload], true),
                remove: { ...state.remove, error: null },
            }
        case FavoriteRecipesActionTypes.RemoveFavoriteRecipeError:
            return { ...state, remove: { ...state.remove, error: action.payload } }

        case FavoriteRecipesActionTypes.TransferRecipeToFavorites:
            if (state.data.favoredRecipes.some(recipe => recipe.id === action.payload.id)) {
                return state
            }

            return {
                ...state,
                data: {
                    ...state.data,
                    favoredRecipes: [...state.data.favoredRecipes, action.payload],
                },
            }
        case FavoriteRecipesActionTypes.UnloadFavoriteRecipes:
            return { ...initialState }
        default:
            return state
    }
}

export default favoriteRecipesReducer
